/* eslint-disable class-methods-use-this */
/* eslint-disable max-classes-per-file */
import React from 'react';
import { connect } from 'react-redux';
import { sortBy } from 'lodash';
import DOMPurify from 'dompurify';
import formatters from '../../../utils/forms/formatters';
import PPAChart from '../charts/ppaChart';
import BaseModal from '../../theme/modals/BaseModal';
import AlertCard from '../../theme/cards/AlertCard';
import api from "../../../store/api";

export class NoteSection extends React.Component {
    static defaultProps = { notes: [] };

    constructor(props) {
        super(props);
        this.state = {
            open: this.props.startOpen,
            isHovered: false,
            chartModalData: null,
        };
    }

    render() {
        let notes = sortBy(this.props.notes || [], ['order', 'title']);
        let rows = notes.map((note) => (
            <tr key={note.title}>
                <td className="is-size-7 has-text-weight-semibold" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(note.title) }} />
                <td className="has-text-grey is-size-7" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(note.note) }} />
            </tr>
        ));

        return (
            <div className={`${this.state.open ? 'mt-3 mb-5' : 'my-3'}`}>
                {this.props.title && (
                    <div
                        className={`is-flex justify-space-between is-align-items-center has-icons-and-text-aligned is-clickable ${this.state.open || this.state.isHovered ? 'has-text-black-bis' : 'has-text-grey'}`}
                        onClick={() => this.setState({ open: !this.state.open })}
                        onMouseEnter={() => this.setState({ isHovered: true })}
                        onMouseLeave={() => this.setState({ isHovered: false })}
                    >
                        <p className="is-size-5">{this.props.title}</p>
                        <i className={`fa ${this.state.open ? 'fa-chevron-down' : 'fa-chevron-left'} mt-2 is-size-5`} />
                    </div>
                )}
                <div className="table-container" style={this.state.open ? { height: 'unset' } : { height: 0 }}>
                    {this.props.children}
                    <table className="table">
                        <tbody>{rows}</tbody>
                    </table>
                </div>
            </div>
        );
    }
}

class LoanEstimateCard extends React.Component {
    static defaultProps = {
        estimate: { attributes: [], project: {} },
        onApply: () => {},
        onShare: () => {},
        onAmortization: () => {},
        showApply: true,
    };

    constructor(props) {
        super(props);
        this.state = {
            chartModalData: null,
            projectEstimate: null,
            peLoading: false,
            peError: null,
        };
    }

    getProjectIdFromProps = (props = this.props) => {
        return props.projectlead?.id || props.applicationData?.projectLead?.id;
    };

    fetchProjectEstimate = async (projectId) => {
        if (!projectId) return;
        this.setState({ peLoading: true, peError: null, projectEstimate: null });
        try {
            const res = await api.get(`/project-estimate/${projectId}/`);
            this.setState({ projectEstimate: res.data, peLoading: false });
        } catch (err) {
            this.setState({ peError: err, peLoading: false, projectEstimate: null });
        }
    };

    componentDidMount() {
        const projectId = this.getProjectIdFromProps();
        if (projectId) this.fetchProjectEstimate(projectId);
    }

    componentDidUpdate(prevProps) {
        const prevProjectId = this.getProjectIdFromProps(prevProps);
        const projectId = this.getProjectIdFromProps(this.props);

        if (projectId && projectId !== prevProjectId) {
            this.fetchProjectEstimate(projectId);
        }
    }

    getAttributeHeaders = () => {
        const type = this.props.estimate?.product?.type;
        if (type === 'PPA') {
            return (
                <thead>
                    <tr>
                        <th>Term (Yrs)</th>
                        <th>Annual Riser</th>
                        <th>Payment (Month)</th>
                        <th>Cost/kWh</th>
                        <th>Riser Chart</th>
                    </tr>
                </thead>
            );
        }
        if (type === 'Solar Power Agreement') {
            return (
                <thead>
                    <tr style={{ textAlign: 'center' }}>
                        <th>Term (Yrs)</th>
                        <th>Earliest year property-owner can take possession</th>
                        <th>Value contributed by this investor</th>
                        <th>Value contributed by Property Owner</th>
                    </tr>
                </thead>
            );
        }
        return (
            <thead>
                <tr>
                    <th>Term (Yrs)</th>
                    <th>Rate</th>
                    {type === 'PACE PPA' && <th>Annual Riser</th>}
                    {this.optionNeedsCapI() && <th>Capital Interest</th>}
                    <th># of paymts/yr</th>
                    <th>Per Paymt</th>
                    <th>Annual Paymt</th>
                    {type === 'PACE PPA' && <th>Cost/kWh</th>}
                    <th> </th>
                    <th>Amortization</th>
                </tr>
            </thead>
        );
    };

    getAttributeRows = () => {
        let rows = [];
        let type = this.props.estimate?.product?.type;
        const appData = this.props.applicationData;
        const { projectEstimate, peLoading } = this.state;

        if (type !== 'Solar Power Agreement') {
            this.props.estimate.attributes
                .sort((a, b) => (a.term > b.term ? 1 : -1))
                .forEach((d) => {
                    let annual_payments = d.schedule.filter((p) => p.year === 1).length;
                    let p1 = d.schedule[0];
                    let annual_cost = p1.payment_w_fees * annual_payments;

                    if (type === 'PPA') {
                        rows.push(
                            <tr key={d.id || d.term}>
                                <td>{d.term}</td>
                                <td>{d.annual_riser || 0}%</td>
                                <td className="has-text-weight-semibold">{formatters.USDCurrency.formatter(p1.payment_w_fees)}</td>
                                <td className="has-text-weight-semibold">${d.cost_per_kwh}</td>
                                {(d.annual_riser || 0) > 0 ? (
                                    <td onClick={() => this.handleChartOpen(d)} className="cursor-is-pointer"><i className="fas fa-chart-area" /></td>
                                ) : <td />}
                            </tr>
                        );
                    } else {
                        rows.push(
                            <tr key={d.id || d.term}>
                                <td>{d.term}</td>
                                <td>{d.rate}%</td>
                                {type === 'PACE PPA' && <td>{d.annual_riser}%</td>}
                                {this.optionNeedsCapI() && <td>{formatters.USDCurrency.formatter(d.capitalized_interest)}</td>}
                                <td>{annual_payments}</td>
                                <td>{formatters.USDCurrency.formatter(p1.payment_w_fees)}</td>
                                <td className="has-text-weight-semibold">{formatters.USDCurrency.formatter(annual_cost)}</td>
                                {type === 'PACE PPA' && <td className="has-text-weight-semibold">${d.cost_per_kwh}</td>}
                                <td>{d.other_info ? <span className="icon has-text-info tooltip"><i className="fas fa-info-circle" /><span className="tooltiptext">{d.other_info}</span></span> : ''}</td>
                                <td onClick={() => this.props.onAmortization(d.id)}><span className="icon has-text-info"><i className="fas fa-list-ol" /></span></td>
                            </tr>
                        );
                    }
                });
        } else {
            if (peLoading || !projectEstimate) {
                return [<tr key="loading-spa" style={{ textAlign: 'center' }}><td colSpan={4}>Loading project estimate…</td></tr>];
            }

            this.props.estimate.attributes
                .sort((a, b) => (a.term > b.term ? 1 : -1))
                .forEach((d) => {
                    const amount = Number(projectEstimate?.project_lead?.amount) || 0;
                    const purchasePricePerDollar = Math.round((Number(d.purchase_price_per_dollar) || 0) * 100) / 100;
                    const taxBenefitPercentage = 0.3;

                    const energyAdderPerDollar = Number(this.props.estimate?.product?.energy_community_adder_per_dollar) || 0;
                    const domesticAdderPerDollar = Number(this.props.estimate?.product?.domestic_content_adder_per_dollar) || 0;

                    const energyITCValue = projectEstimate?.project_lead?.energyITCAdderValue ?? 0;
                    const domesticITCValue = projectEstimate?.project_lead?.domesticITCAdderValue ?? 0;


                    let total_added_per_dollar = 0;
                    if (energyITCValue === 10 && energyAdderPerDollar > 0) {
                        const energyCalc = amount * 0.1 * energyAdderPerDollar;
                        total_added_per_dollar += energyCalc;
                    }
                    if (domesticITCValue === 10 && domesticAdderPerDollar > 0) {
                        const domesticCalc = amount * 0.1 * domesticAdderPerDollar;
                        total_added_per_dollar += domesticCalc;
                    }

                    const investor_contrib = (amount * taxBenefitPercentage * purchasePricePerDollar) + total_added_per_dollar;
                    const owner_contrib = amount - investor_contrib;

                    rows.push(
                        <tr key={d.id || d.term} style={{ textAlign: 'center' }}>
                            <td>{d.term}</td>
                            <td>{d.earliest_year}</td>
                            <td>{formatters.USDCurrency.formatter(investor_contrib)}</td>
                            <td>{formatters.USDCurrency.formatter(owner_contrib)}</td>
                        </tr>
                    );
                });
        }
        return rows;
    };

    getTitle() {
        if (this.props.title) return this.props.title;
        if (this.props.showType) return this.props.estimate?.product?.type;
        return 'Option ' + this.props.index;
    }

    optionNeedsCapI = () => this.props.estimate?.attributes.some((d) => d.capitalized_interest);

    handleChartOpen = (attribute) => this.setState({ chartModalData: attribute.schedule });

    handleChartClose = () => this.setState({ chartModalData: null });

    getClosingCostsList = () => {
        const fees_list = this.props.estimate?.one_time_fees_list || [];
        if (!fees_list.length) return <></>;
        let notes = fees_list.map((fee) => ({ title: fee.name, note: formatters.USDCurrency.formatter(fee.amount) }));
        const total_costs = fees_list.reduce((r, d) => r + d.amount, 0);
        let title = (
            <>
                <div>Closing Costs: {formatters.USDCurrency.formatter(total_costs)}</div>
                <div className="is-size-7 is-italic is-light mt-3">Capitalized/Included in Monthly Payments</div>
            </>
        );
        return <NoteSection title={title} notes={notes} startOpen />;
    };

    getRequiredDocsList = () => {
        const docs_list = this.props.estimate.required_docs || [];
        if (!docs_list.length) return <></>;
        const notes = docs_list.map((d) => ({
            title: d.name,
            note: '',
        }));
        return (
            <NoteSection title="Documents Required to Apply" notes={notes} startOpen />
        );
    };

    render() {
        let type = this.props.estimate?.product?.type;
        if (!type) return <></>;
        return (
            <div className="card loan-estimate-card mb-6">
                {this.props.header}
                <div className="card-content">
                    <div className="content">
                        <div className="level mb-4 no-print">
                            <div className="level-left">
                                <div className="level-item">
                                    <div>
                                        <div className="title is-size-4 has-text-primary">{this.getTitle()}</div>
                                        {this.props.estimate?.product?.description && (
                                            <div className="subtitle is-size-6 mt-1" style={{ color: '#294d85' }}>{this.props.estimate.product.description}</div>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="level-right">
                                <div className="level-item">
                                    {!!this.props.hasPriviledges && <button className="button is-primary mr-3" onClick={() => this.props.onShare()}>Share</button>}
                                    {this.props.showApply && <button className="button is-link" onClick={() => this.props.onApply()}>Apply for this product</button>}
                                </div>
                            </div>
                        </div>
                        <div className="table-container mb-6">
                            <table className="table is-striped">
                                {this.getAttributeHeaders()}
                                <tbody>{this.getAttributeRows()}</tbody>
                            </table>
                        </div>
                        {this.props.tax_benefit && type !== 'Solar Power Agreement' && (
                            <div className="mb-6">
                                <AlertCard type="info">
                                    <span>
                                        The payments above are before the ITC pay-down you are
                                        anticipating at the end of year 1. To see the new payments
                                        after that payment,{' '}
                                        <strong>open the amortization schedule</strong>{' '}
                                        <i className="fas fa-list-ol" style={{ color: '#294d85' }} />{' '}
                                        and scroll down to the second year's adjusted payments.
                                    </span>
                                </AlertCard>
                            </div>
                        )}
                    </div>
                    {this.getClosingCostsList()}
                    {!!Object.keys(this.props.estimate?.admin_info || {}).length &&
						!!this.props.is_admin && (
							<NoteSection
								title="Admin Info"
								notes={Object.keys(this.props.estimate?.admin_info).map(
									(key) => ({
										title: key,
										note: this.props.estimate.admin_info[key],
									})
								)}
							/>
						)}
                    <NoteSection title="Notes about this option" notes={this.props.estimate?.notes} startOpen />
                    {this.getRequiredDocsList()}
                </div>
                {this.state.chartModalData && (
                    <BaseModal visible onClose={this.handleChartClose} cardStyle={{ width: '60vw' }}>
                        <div style={{ height: '30vw', minHeight: '350px', width: '100%' }}>
                            <PPAChart data={this.state.chartModalData} />
                        </div>
                    </BaseModal>
                )}
            </div>
        );
    }
}

const mapStateToProps = (state) => ({
    is_admin: state.user.decodedTokenData?.admin,
    applicationData: state.applicationData,
});

export default connect(mapStateToProps)(LoanEstimateCard);